/**
 * Contraste WCAG 2.1 1.4.3 (texte normal : 4,5:1).
 * Sert à prévenir dans les settings et à proposer une teinte acceptable —
 * jamais à forcer la couleur à l'affichage.
 */

const WHITE: [number, number, number] = [255, 255, 255]

/** Seuil AA texte normal, avec une marge pour l'arrondi CSS / axe. */
export const WCAG_AA_NORMAL_TEXT = 4.5
const SUGGEST_MIN_RATIO = 4.55

function channelToLin(c: number): number {
  const s = c / 255
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function parseHexColor(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, '')
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return [parseInt(raw[0] + raw[0], 16), parseInt(raw[1] + raw[1], 16), parseInt(raw[2] + raw[2], 16)]
  }
  if (/^[0-9a-f]{6}$/i.test(raw)) {
    return [parseInt(raw.slice(0, 2), 16), parseInt(raw.slice(2, 4), 16), parseInt(raw.slice(4, 6), 16)]
  }
  return null
}

export function relativeLuminance([r, g, b]: [number, number, number]): number {
  return 0.2126 * channelToLin(r) + 0.7152 * channelToLin(g) + 0.0722 * channelToLin(b)
}

export function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const l1 = relativeLuminance(a)
  const l2 = relativeLuminance(b)
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1]
  return (hi + 0.05) / (lo + 0.05)
}

export function meetsTextContrast(hex: string, background: [number, number, number] = WHITE, min = WCAG_AA_NORMAL_TEXT): boolean {
  const rgb = parseHexColor(hex)
  if (!rgb) return false
  return contrastRatio(rgb, background) >= min
}

function toHex([r, g, b]: [number, number, number]): string {
  const h = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`
}

export function textContrastRatio(hex: string, background: [number, number, number] = WHITE): number | null {
  const rgb = parseHexColor(hex)
  if (!rgb) return null
  return contrastRatio(rgb, background)
}

/** Teinte proposée (assombrie vers le noir) pour atteindre `min` sur blanc. */
export function suggestTextContrast(hex: string, min = SUGGEST_MIN_RATIO): string {
  const rgb = parseHexColor(hex)
  if (!rgb) return hex
  if (contrastRatio(rgb, WHITE) >= min) return toHex(rgb)

  let lo = 0
  let hi = 1
  let best: [number, number, number] = [0, 0, 0]
  for (let i = 0; i < 24; i++) {
    const t = (lo + hi) / 2
    const mixed: [number, number, number] = [rgb[0] * (1 - t), rgb[1] * (1 - t), rgb[2] * (1 - t)]
    if (contrastRatio(mixed, WHITE) >= min) {
      hi = t
      best = mixed
    } else {
      lo = t
    }
  }
  return toHex(best)
}
